
import { useState } from "react";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Eye,
  FileText,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Mail,
  MailOpen,
} from "lucide-react";
import { toast } from "sonner";
import { exportRequestToPDF } from "@/utils/pdfExport";
import { type ItineraryRequest } from "@/hooks/useItineraryRequests";

type ItineraryRequestsTableProps = {
  requests: ItineraryRequest[] | undefined;
  isLoading: boolean;
  onViewDetails: (request: ItineraryRequest) => void;
  onUpdateStatus: (id: string, status: string) => void;
  onToggleReadStatus: (request: ItineraryRequest) => void;
  onToggleSelection: (id: string) => void;
  isSelected: (id: string) => boolean;
};

export function ItineraryRequestsTable({
  requests,
  isLoading,
  onViewDetails,
  onUpdateStatus,
  onToggleReadStatus,
  onToggleSelection,
  isSelected,
}: ItineraryRequestsTableProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <Badge variant="default">New</Badge>;
      case "in-progress":
        return <Badge variant="secondary">In Progress</Badge>;
      case "converted":
        return <Badge variant="success">Converted</Badge>;
      case "declined":
        return <Badge variant="destructive">Declined</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const handleExportPDF = (request: ItineraryRequest) => {
    try {
      exportRequestToPDF(request);
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Destination</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-[100px] text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && (
          Array(5).fill(0).map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton className="h-5 w-[80px]" /></TableCell>
              <TableCell><Skeleton className="h-5 w-[120px]" /></TableCell>
              <TableCell><Skeleton className="h-5 w-[150px]" /></TableCell>
              <TableCell><Skeleton className="h-5 w-[100px]" /></TableCell>
              <TableCell><Skeleton className="h-9 w-[100px]" /></TableCell>
            </TableRow>
          ))
        )}
        
        {!isLoading && requests && requests.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="h-24 text-center">
              No itinerary requests found
            </TableCell>
          </TableRow>
        )}
        
        {!isLoading && requests && requests.map((request) => (
          <TableRow 
            key={request.id} 
            className={`${request.status === 'new' ? 'bg-muted/40' : ''} ${isSelected(request.id) ? 'bg-primary/10' : ''}`}
            onClick={() => onToggleSelection(request.id)}
          >
            <TableCell>
              {format(new Date(request.created_at), 'MMM d, yyyy')}
            </TableCell>
            <TableCell>{request.name}</TableCell>
            <TableCell>{request.destination}</TableCell>
            <TableCell>{getStatusBadge(request.status)}</TableCell>
            <TableCell>
              <div className="flex space-x-2 justify-end">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleReadStatus(request);
                  }}
                  title={request.status === 'new' ? "Mark as Read" : "Mark as Unread"}
                >
                  {request.status === 'new' ? (
                    <Mail className="h-4 w-4" />
                  ) : (
                    <MailOpen className="h-4 w-4" />
                  )}
                  <span className="sr-only">
                    {request.status === 'new' ? "Mark as Read" : "Mark as Unread"}
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(request);
                  }}
                  title="View Details"
                >
                  <Eye className="h-4 w-4" />
                  <span className="sr-only">View Details</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportPDF(request);
                  }}
                  title="Export PDF"
                >
                  <FileText className="h-4 w-4" />
                  <span className="sr-only">Export PDF</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" title="Update Status">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">Update Status</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => onUpdateStatus(request.id, 'new')}
                      disabled={request.status === 'new'}
                    >
                      <span className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        Mark as New
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => onUpdateStatus(request.id, 'in-progress')}
                      disabled={request.status === 'in-progress'}
                    >
                      <span className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        Mark as In Progress
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => onUpdateStatus(request.id, 'converted')}
                      disabled={request.status === 'converted'}
                    >
                      <span className="flex items-center">
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Mark as Converted
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => onUpdateStatus(request.id, 'declined')}
                      disabled={request.status === 'declined'}
                    >
                      <span className="flex items-center">
                        <XCircle className="mr-2 h-4 w-4" />
                        Mark as Declined
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleExportPDF(request)}>
                      <span className="flex items-center">
                        <Download className="mr-2 h-4 w-4" />
                        Export as PDF
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
